import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    setLoaded(true);
  }, []);

  // Cursor-tracked movement gives the AI photo a subtle parallax.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame;
    const onMove = (event) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!heroRef.current) return;
        const x = (event.clientX / window.innerWidth - 0.5) * 14;
        const y = (event.clientY / window.innerHeight - 0.5) * 10;
        heroRef.current.style.setProperty("--mx", `${x}px`);
        heroRef.current.style.setProperty("--my", `${y}px`);
      });
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      aria-labelledby="hero-title"
      className="relative isolate min-h-[100svh] w-full overflow-hidden bg-[var(--bg)]"
    >
      {/* AI image — full-bleed background, anchored to the right where the artist stands. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 -z-30 transition-opacity duration-[1600ms] ease-out motion-reduce:transition-none ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        style={{
          backgroundImage: "url(/assets/images/AI_IMAGE.png)",
          backgroundSize: "cover",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
          transform: loaded
            ? "translate3d(calc(var(--mx, 0px) * 0.35), calc(var(--my, 0px) * -0.25), 0) scale(1.04)"
            : "scale(1.08)",
          willChange: "transform, opacity",
          transition: "transform 1600ms cubic-bezier(0.22, 1, 0.36, 1), opacity 1600ms ease-out",
        }}
      />

      {/* Deep navy wash on top of the AI image so the photo blends with the brand palette. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20"
        style={{
          background:
            "linear-gradient(115deg, rgba(8,19,47,0.95) 0%, rgba(8,19,47,0.85) 28%, rgba(8,19,47,0.55) 52%, rgba(8,19,47,0.25) 78%, rgba(8,19,47,0.35) 100%)",
        }}
      />

      {/* Lateral fade — strong on the left for readability, softens into the photo on the right. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20"
        style={{
          background:
            "radial-gradient(ellipse 70% 80% at 22% 50%, rgba(8,19,47,0.85), transparent 70%)",
        }}
      />

      {/* Soft copper accent glow behind the lettering. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(circle at 30% 55%, rgba(184,111,74,0.18), transparent 45%)",
        }}
      />

      {/* Hairline horizon */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 -z-10 hidden h-px bg-gradient-to-r from-transparent via-[var(--clay)]/25 to-transparent md:block"
      />

      {/* Editorial frame details */}
      <span className="absolute left-[3%] top-[11%] z-30 hidden h-4 w-4 border-l border-t border-[var(--clay)]/70 md:block" />
      <span className="absolute right-[3%] top-[11%] z-30 hidden h-4 w-4 border-r border-t border-[var(--clay)]/70 md:block" />
      <span className="absolute bottom-[9%] left-[3%] z-30 hidden h-4 w-4 border-b border-l border-[var(--clay)]/70 md:block" />
      <span className="absolute bottom-[9%] right-[3%] z-30 hidden h-4 w-4 border-b border-r border-[var(--clay)]/70 md:block" />

      <div
        aria-hidden="true"
        className="absolute right-[3.2%] top-[25%] z-30 hidden items-center gap-3 text-[9px] uppercase tracking-[0.42em] text-[var(--bone)]/45 xl:flex"
      >
        <span className="h-px w-8 bg-[var(--clay)]/70" />
        Art made by hand · 01
      </div>

      {/* Main editorial lock-up — anchored to the left half, leaving the photo room on the right. */}
      <div className="relative z-20 mx-auto flex min-h-[100svh] w-full max-w-[1700px] items-center px-5 pb-28 pt-28 sm:px-8 md:px-12 md:pb-24 md:pt-32 lg:px-16">
        <div className="w-full md:w-[68%] lg:w-[58%] xl:w-[54%] 2xl:max-w-[820px]">
          <div
            className={`flex flex-col items-center text-center transition-[opacity,transform] duration-[1200ms] ease-out motion-reduce:transition-none md:items-start md:text-left ${
              loaded ? "translate-y-0 opacity-100" : "translate-y-7 opacity-0"
            }`}
            style={{ transitionDelay: "120ms" }}
          >
            <div className="mb-5 flex items-center gap-3 text-[9px] uppercase tracking-[0.34em] text-[var(--bone)]/70 sm:text-[10px] md:mb-6 md:ml-[8%] md:gap-4 md:tracking-[0.42em]">
              <span className="h-px w-7 bg-[var(--clay)] sm:w-10" />
              <span className="whitespace-nowrap">Est. 2018 · Delhi</span>
              <span className="h-px w-7 bg-[var(--clay)] sm:w-10" />
            </div>

            {/* The copper initials form T-A-B-A down the page. */}
            <h1
              id="hero-title"
              aria-label="The Art By Artist"
              className="font-display w-full select-none text-[clamp(4rem,9.2vw,9.75rem)] font-light uppercase leading-[0.79] tracking-[-0.055em]"
              style={{ textShadow: "0 10px 45px rgba(4, 8, 20, 0.55)" }}
            >
              <span aria-hidden="true" className="block md:pl-[2%]">
                <span className="text-[var(--clay)]">T</span>
                <span className="text-[var(--bone)]">he</span>
              </span>
              <span aria-hidden="true" className="block md:pl-[13%]">
                <span className="text-[var(--clay)]">A</span>
                <span className="text-[var(--bone)]">rt</span>
              </span>
              <span aria-hidden="true" className="block md:pl-[36%]">
                <span className="text-[var(--clay)]">B</span>
                <span className="text-[var(--bone)]">y</span>
              </span>
              <span aria-hidden="true" className="block md:pl-[4%]">
                <span className="text-[var(--clay)]">A</span>
                <span className="text-[var(--bone)]">rtist</span>
              </span>
            </h1>

            <div className="mt-6 max-w-[520px] md:ml-[10%] md:mt-8">
              <p className="text-sm leading-relaxed text-[var(--muted)] sm:text-[15px]">
                <span className="font-display text-lg italic text-[var(--bone)] sm:text-xl">
                  Hand-painted stories
                </span>{" "}
                on walls, ceilings and façades — every mural sketched, scaled
                and brushed by hand.
              </p>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-3 md:mt-8 md:justify-start">
                <Link
                  to="/gallery"
                  className="group inline-flex items-center gap-3 rounded-full bg-[var(--clay)] px-6 py-3.5 text-xs uppercase tracking-[0.18em] text-[var(--bone)] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[var(--bone)] hover:text-[var(--ink)] hover:shadow-[0_18px_45px_rgba(0,0,0,0.3)] sm:px-7"
                >
                  See the walls
                  <span className="relative inline-block h-4 w-4 overflow-hidden" aria-hidden="true">
                    <span className="absolute inset-0 transition-transform duration-500 group-hover:translate-x-5 group-hover:-translate-y-5">
                      →
                    </span>
                    <span className="absolute inset-0 -translate-x-5 translate-y-5 transition-transform duration-500 group-hover:translate-x-0 group-hover:translate-y-0">
                      →
                    </span>
                  </span>
                </Link>
                <Link
                  to="/booknow"
                  className="group inline-flex items-center gap-3 rounded-full border border-[var(--bone)]/40 bg-[var(--bg)]/10 px-6 py-3.5 text-xs uppercase tracking-[0.18em] text-[var(--bone)] backdrop-blur-[2px] transition-all duration-500 hover:-translate-y-0.5 hover:border-[var(--bone)] hover:bg-[var(--bone)] hover:text-[var(--ink)] sm:px-7"
                >
                  Commission a wall
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom studio notes */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 px-5 pb-5 sm:px-8 md:px-10 md:pb-7">
        <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.28em] text-[var(--bone)]/65 sm:text-[10px] md:tracking-[0.34em]">
          <span>— The Art By Artist</span>
          <span className="hidden items-center gap-2 md:flex">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--clay)] animate-pulse" />
            Studio open · Delhi NCR
          </span>
          <span>Scroll ↓</span>
        </div>
      </div>

      {/* Fine paper grain kept above imagery but below all interaction. */}
      <div className="pointer-events-none absolute inset-0 z-40 bg-noise opacity-[0.05] mix-blend-overlay" />
    </section>
  );
}
